/**
 * Salary calculation utilities
 * Handles conversions between gross and net salaries
 */

import { WORKING_HOURS, SOCIAL_CONTRIBUTION_RATES } from '../constants/salaryConstants';

/**
 * Calculate net salary from gross salary
 * @param {number} grossValue - Gross salary amount
 * @param {string} status - Professional status
 * @param {string} workTime - Work time type ('temps_plein' | 'temps_partiel')
 * @param {number} partTimePercentage - Percentage for part-time work (default: 100)
 * @returns {number} Net salary amount
 */
export const convertGrossToNet = (grossValue, status, workTime = 'temps_plein', partTimePercentage = 100) => {
  if (!grossValue || !SOCIAL_CONTRIBUTION_RATES[status]) {
    return 0;
  }

  const rates = SOCIAL_CONTRIBUTION_RATES[status];
  const totalDeductions = rates.cotisations_sociales + rates.csg_crds + rates.autres_deductions;
  
  let netValue = grossValue * (1 - totalDeductions);
  
  // Apply part-time adjustment
  if (workTime === 'temps_partiel') {
    netValue = netValue * (partTimePercentage / 100);
  }
  
  return Math.round(netValue * 100) / 100;
};

/**
 * Calculate gross salary from net salary
 * @param {number} netValue - Net salary amount
 * @param {string} status - Professional status
 * @param {string} workTime - Work time type ('temps_plein' | 'temps_partiel')
 * @param {number} partTimePercentage - Percentage for part-time work (default: 100)
 * @returns {number} Gross salary amount
 */
export const convertNetToGross = (netValue, status, workTime = 'temps_plein', partTimePercentage = 100) => {
  if (!netValue || !SOCIAL_CONTRIBUTION_RATES[status]) {
    return 0;
  }

  const rates = SOCIAL_CONTRIBUTION_RATES[status];
  const totalDeductions = rates.cotisations_sociales + rates.csg_crds + rates.autres_deductions;
  
  let grossValue = netValue / (1 - totalDeductions);
  
  // Apply part-time adjustment
  if (workTime === 'temps_partiel') {
    grossValue = grossValue / (partTimePercentage / 100);
  }
  
  return Math.round(grossValue * 100) / 100;
};

/**
 * Calculate all salary fields from a single input field
 * @param {string} changedField - The field that was modified
 * @param {number} value - The new value
 * @param {string} status - Professional status
 * @param {string} workTime - Work time type
 * @param {number} partTimePercentage - Part-time percentage
 * @returns {object} Object containing all calculated salary fields
 */
export const calculateAllSalaryFields = (changedField, value, status, workTime, partTimePercentage) => {
  if (!value || value === '') {
    return {
      horaireBrut: '',
      mensuelBrut: '',
      annuelBrut: '',
      horaireNet: '',
      mensuelNet: '',
      annuelNet: ''
    };
  }

  const val = parseFloat(value);
  let horaireBrut, mensuelBrut, annuelBrut, horaireNet, mensuelNet, annuelNet;

  switch (changedField) {
    case 'horaireBrut':
      horaireBrut = val;
      mensuelBrut = Math.round(val * WORKING_HOURS.HOURS_PER_MONTH * 100) / 100;
      annuelBrut = Math.round(val * WORKING_HOURS.HOURS_PER_YEAR * 100) / 100;
      horaireNet = convertGrossToNet(val, status, workTime, partTimePercentage);
      mensuelNet = convertGrossToNet(mensuelBrut, status, workTime, partTimePercentage);
      annuelNet = convertGrossToNet(annuelBrut, status, workTime, partTimePercentage);
      break;
    
    case 'mensuelBrut':
      mensuelBrut = val;
      horaireBrut = Math.round(val / WORKING_HOURS.HOURS_PER_MONTH * 100) / 100;
      annuelBrut = Math.round(val * 12 * 100) / 100;
      horaireNet = convertGrossToNet(horaireBrut, status, workTime, partTimePercentage);
      mensuelNet = convertGrossToNet(val, status, workTime, partTimePercentage);
      annuelNet = convertGrossToNet(annuelBrut, status, workTime, partTimePercentage);
      break;
    
    case 'annuelBrut':
      annuelBrut = val;
      mensuelBrut = Math.round(val / 12 * 100) / 100;
      horaireBrut = Math.round(val / WORKING_HOURS.HOURS_PER_YEAR * 100) / 100;
      horaireNet = convertGrossToNet(horaireBrut, status, workTime, partTimePercentage);
      mensuelNet = convertGrossToNet(mensuelBrut, status, workTime, partTimePercentage);
      annuelNet = convertGrossToNet(val, status, workTime, partTimePercentage);
      break;
    
    case 'horaireNet':
      horaireNet = val;
      horaireBrut = convertNetToGross(val, status, workTime, partTimePercentage);
      mensuelNet = Math.round(val * WORKING_HOURS.HOURS_PER_MONTH * 100) / 100;
      annuelNet = Math.round(val * WORKING_HOURS.HOURS_PER_YEAR * 100) / 100;
      mensuelBrut = convertNetToGross(mensuelNet, status, workTime, partTimePercentage);
      annuelBrut = convertNetToGross(annuelNet, status, workTime, partTimePercentage);
      break;
    
    case 'mensuelNet':
      mensuelNet = val;
      mensuelBrut = convertNetToGross(val, status, workTime, partTimePercentage);
      horaireNet = Math.round(val / WORKING_HOURS.HOURS_PER_MONTH * 100) / 100;
      annuelNet = Math.round(val * 12 * 100) / 100;
      horaireBrut = convertNetToGross(horaireNet, status, workTime, partTimePercentage);
      annuelBrut = convertNetToGross(annuelNet, status, workTime, partTimePercentage);
      break;
    
    case 'annuelNet':
      annuelNet = val;
      mensuelNet = Math.round(val / 12 * 100) / 100;
      horaireNet = Math.round(val / WORKING_HOURS.HOURS_PER_YEAR * 100) / 100;
      mensuelBrut = convertNetToGross(mensuelNet, status, workTime, partTimePercentage);
      horaireBrut = convertNetToGross(horaireNet, status, workTime, partTimePercentage);
      annuelBrut = convertNetToGross(val, status, workTime, partTimePercentage);
      break;
    
    default:
      return {
        horaireBrut: '',
        mensuelBrut: '',
        annuelBrut: '',
        horaireNet: '',
        mensuelNet: '',
        annuelNet: ''
      };
  }

  return {
    horaireBrut: horaireBrut?.toString() || '',
    mensuelBrut: mensuelBrut?.toString() || '',
    annuelBrut: annuelBrut?.toString() || '',
    horaireNet: horaireNet?.toString() || '',
    mensuelNet: mensuelNet?.toString() || '',
    annuelNet: annuelNet?.toString() || ''
  };
};